import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLES = [
  { value: "athlete", label: "Athlete" },
  { value: "coach", label: "Coach" },
  { value: "club", label: "Club / Team" },
  { value: "other", label: "Other" },
];

export default function WaitlistForm() {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("athlete");
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState(null);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/waitlist/count`)
      .then((r) => setCount(r.data.count))
      .catch(() => {});
  }, [done]);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setLoading(true);
    try {
      await axios.post(`${API}/waitlist`, { email, role });
      setDone(true);
      toast.success("You're on the list. We'll be in touch.");
      setEmail("");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (err?.response?.status === 409) {
        toast.error("This email is already on the waitlist.");
      } else {
        toast.error(detail || "Something went wrong. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="glass relative w-full overflow-hidden p-8 md:p-10" data-testid="waitlist-card">
      <div className="pointer-events-none absolute -right-20 -top-20 h-60 w-60 rounded-full bg-[#ff5500]/20 blur-3xl" />
      <div className="relative">
        <div className="label-mono">01 / Early Access</div>
        <h3 className="font-display mt-3 text-3xl font-semibold tracking-tight md:text-4xl">
          Join the waitlist
        </h3>
        <p className="mt-3 max-w-md text-sm text-white/60">
          Be the first to get the Arvex capture unit when we ship to selected
          clubs and athletes. No spam, just one update when we launch.
        </p>

        {done ? (
          <div
            data-testid="waitlist-success"
            className="mt-8 border border-[#00e5ff]/30 bg-[#00e5ff]/5 px-5 py-4"
          >
            <div className="label-mono">Confirmed</div>
            <p className="mt-2 text-sm text-white/80">
              You&apos;re in. Check your inbox — we&apos;ll reach out before the next
              winter season.
            </p>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-8 space-y-4">
            <div className="flex flex-wrap gap-2">
              {ROLES.map((r) => (
                <button
                  type="button"
                  key={r.value}
                  data-testid={`waitlist-role-${r.value}`}
                  onClick={() => setRole(r.value)}
                  className={`font-mono text-[10px] uppercase tracking-[0.22em] px-3 py-2 border transition-colors ${
                    role === r.value
                      ? "border-[#ff5500] bg-[#ff5500]/10 text-white"
                      : "border-white/10 text-white/60 hover:border-white/30"
                  }`}
                >
                  {r.label}
                </button>
              ))}
            </div>

            <div className="flex flex-col gap-3 sm:flex-row">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@team.cc"
                data-testid="waitlist-email-input"
                className="flex-1 border border-white/10 bg-black/40 px-4 py-3 font-mono text-sm text-white placeholder:text-white/30 focus:border-[#00e5ff] focus:outline-none focus:ring-1 focus:ring-[#00e5ff]"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="waitlist-submit-button"
                className="btn-primary px-6 py-3 font-mono text-xs uppercase tracking-[0.22em] disabled:opacity-60"
              >
                {loading ? "Submitting…" : "Request Access"}
              </button>
            </div>

            {count !== null && (
              <div
                className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/40"
                data-testid="waitlist-count"
              >
                <span className="text-[#00e5ff]">{String(count).padStart(3, "0")}</span>{" "}
                people already on the list
              </div>
            )}
          </form>
        )}
      </div>
    </div>
  );
}
